/**
 * The postcard's backdrops, one per place the idea roll can land on
 * (data/idea-shapes.ts, by its `art` key): a sky, a ground and one
 * ornament, drawn as plain shapes by components/IdeaPostcard.tsx. A place they
 * wrote themselves gets MYSTERY instead: a dark violet stage with a lit door in
 * it, the present they will open when the crew draw the picture.
 */
export type Ornament = 'moon' | 'fish' | 'clouds' | 'peaks' | 'school' | 'dunes' | 'dome' | 'stars' | 'sun' | 'rain' | 'trees' | 'island' | 'door';

export interface Scene {
  /** top and bottom of the sky */
  sky: [string, string];
  ground: string;
  ornament: Ornament;
  /** the figures, on a dark sky, are drawn pale so they still read */
  dark?: boolean;
}

export const SCENES: Record<string, Scene> = {
  moon: { sky: ['#0F1E4D', '#2A3F8A'], ground: '#C9CFDB', ornament: 'moon', dark: true },
  fish: { sky: ['#1E7FA8', '#0E4F78'], ground: '#D9C98F', ornament: 'fish', dark: true },
  clouds: { sky: ['#9FD1F7', '#DDF1FF'], ground: '#F6FBFF', ornament: 'clouds' },
  peaks: { sky: ['#7DB6E8', '#D7ECFB'], ground: '#F3F7FB', ornament: 'peaks' },
  school: { sky: ['#BFD9E8', '#E8EEF1'], ground: '#B9A27A', ornament: 'school' },
  dunes: { sky: ['#F7B46A', '#FFE2B0'], ground: '#E8B36A', ornament: 'dunes' },
  dome: { sky: ['#D7E6F5', '#F4F8FC'], ground: '#E6EEF8', ornament: 'dome' },
  stars: { sky: ['#0B1540', '#1E2B66'], ground: '#2C3A5E', ornament: 'stars', dark: true },
  sun: { sky: ['#F68FA7', '#FFD3A0'], ground: '#9CB7A3', ornament: 'sun' },
  rain: { sky: ['#6F7F99', '#A9B5C8'], ground: '#6E8A7B', ornament: 'rain', dark: true },
  trees: { sky: ['#9CCB95', '#D4EBC6'], ground: '#4F8A4B', ornament: 'trees' },
  island: { sky: ['#8ED3F2', '#D8F1FB'], ground: '#2E9ACF', ornament: 'island' },
};

/** A place of their own: somewhere nobody has drawn yet, behind a lit door. */
export const MYSTERY: Scene = { sky: ['#2B1D57', '#5B3F98'], ground: '#241848', ornament: 'door', dark: true };

/** The backdrop for a place: its own scene when the dice dealt it, else the mystery. */
export function sceneFor(art: string | undefined): Scene {
  return (art && SCENES[art]) || MYSTERY;
}
